import React, { useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Layers, GitMerge, SlidersHorizontal, Check, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MODE_META = {
  standalone: {
    label: 'Standalone',
    icon: Layers,
    color: '#0e7490',
    desc: 'All 5 pillars score the LEI — Pillar 5 (Business Impact) contributes 10%.',
  },
  conjoined: {
    label: 'Conjoined with Missing Middle',
    icon: GitMerge,
    color: '#7e22ce',
    desc: 'LEI index = Pillars 1–4 (renormalised). Pillar 5 is shown as non-weighted reinforcement, routed to the value chain to avoid double counting.',
  },
};

/**
 * Mode badge + admin preview/switch panel for the LEI calculation mode.
 * Non-admins see the badge + explainer only. Admins can preview both modes
 * side-by-side (read-only) and commit one org-wide.
 */
export const LeiModeControls = ({ currentMode = 'standalone', selectedOrg, isAdmin = false, onCommitted }) => {
  const [open, setOpen] = useState(false);
  const [cmp, setCmp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const meta = MODE_META[currentMode] || MODE_META.standalone;
  const ModeIcon = meta.icon;
  const orgParam = selectedOrg && selectedOrg !== 'all' ? `&organization_id=${selectedOrg}` : '';
  const token = secureStorage.getItem('session_token');

  const loadCompare = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        authedFetch(`${API_URL}/api/lei-health/overview?preview_mode=standalone${orgParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        authedFetch(`${API_URL}/api/lei-health/overview?preview_mode=conjoined${orgParam}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCmp({ standalone: await a.json(), conjoined: await b.json() });
    } catch (e) {
      toast.error('Could not load mode comparison');
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !cmp) loadCompare();
  };

  const commit = async (mode) => {
    setCommitting(true);
    try {
      const res = await authedFetch(`${API_URL}/api/modelling-settings/lei-mode`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lei_mode: mode, ...(selectedOrg && selectedOrg !== 'all' ? { organization_id: selectedOrg } : {}) }),
      });
      if (res.ok) {
        toast.success(`LEI scoring set to "${MODE_META[mode].label}" org-wide`);
        setCmp(null);
        setOpen(false);
        onCommitted?.(mode);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Could not update mode');
      }
    } finally {
      setCommitting(false);
    }
  };

  const scoreOf = (m) => (cmp?.[m]?.overall_score != null ? Number(cmp[m].overall_score).toFixed(1) : '—');

  return (
    <Card className="border-l-4" style={{ borderLeftColor: meta.color }} data-testid="lei-mode-controls">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ModeIcon size={16} style={{ color: meta.color }} />
              <span className="text-sm font-semibold text-slate-700">LEI Scoring Mode</span>
              <Badge data-testid="lei-mode-badge" style={{ backgroundColor: meta.color + '18', color: meta.color, borderColor: meta.color + '40' }} className="border">
                {meta.label}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">{meta.desc}</p>
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={toggleOpen} data-testid="lei-mode-preview-toggle" className="gap-2 shrink-0">
              <SlidersHorizontal size={14} />
              {open ? 'Hide comparison' : 'Preview & switch'}
            </Button>
          )}
        </div>

        {open && isAdmin && (
          <div className="mt-4 border-t pt-4" data-testid="lei-mode-compare-panel">
            <p className="text-xs text-slate-500 mb-3">Read-only preview of the headline LEI under each mode. Committing changes how LEI is scored org-wide.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['standalone', 'conjoined'].map((m) => {
                const mm = MODE_META[m];
                const active = currentMode === m;
                return (
                  <div key={m} className={`rounded-lg border p-3 ${active ? 'ring-2' : ''}`} style={active ? { borderColor: mm.color, boxShadow: `0 0 0 1px ${mm.color}` } : {}} data-testid={`lei-mode-compare-${m}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">{mm.label}</span>
                      {active && <Badge variant="secondary" className="text-[10px]">Current</Badge>}
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-3xl font-bold" style={{ color: mm.color }} data-testid={`lei-mode-score-${m}`}>
                        {loading ? '…' : scoreOf(m)}
                      </span>
                      <span className="text-xs text-slate-400">overall LEI</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {m === 'conjoined' ? 'Pillars 1–4 · P5 reinforcement' : 'Pillars 1–5 weighted'}
                    </div>
                    <Button
                      size="sm"
                      className="w-full mt-3 gap-1"
                      variant={active ? 'secondary' : 'default'}
                      disabled={active || committing || loading}
                      onClick={() => commit(m)}
                      data-testid={`lei-mode-apply-${m}`}
                    >
                      {active ? <><Check size={13} /> In use</> : <>Apply org-wide <ArrowRight size={13} /></>}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeiModeControls;
